import { useInitiateContextFileUploadMutation } from "src/graphql/types";
import type { StagedFile } from "./types";

/**
 * Uploads a staged context file directly to object storage via a presigned
 * URL and returns the storage key to pass on the create/update mutation
 * (`contextFile.file.s3Key`). The backend verifies the uploaded object
 * (workspace prefix + size) before persisting anything, so a PUT that never
 * gets saved is harmless.
 *
 * This replaces base64-encoding file bytes into the GraphQL body, which
 * caps usable file size well below the 50MB base64 ceiling.
 */
export const useUploadContextFile = () => {
  // Minting an upload URL changes no queryable data, so opt out of the global
  // mutation default that invalidates every active query.
  const { mutateAsync: initiateUpload } = useInitiateContextFileUploadMutation({
    onSuccess: () => {},
  });

  /** `signal` aborts the transfer, e.g. when the user cancels an upload. */
  return async (
    stagedFile: StagedFile,
    signal?: AbortSignal,
  ): Promise<string> => {
    const { initiateContextFileUpload: initiated } = await initiateUpload({
      input: {
        filename: stagedFile.filename,
        filetype: stagedFile.filetype,
        fileSizeBytes: stagedFile.filesize,
      },
    });

    const uploadResponse = await fetch(initiated.uploadUrl, {
      method: "PUT",
      body: stagedFile.file,
      headers: { "Content-Type": stagedFile.filetype },
      signal,
    });
    if (!uploadResponse.ok) {
      throw new Error(
        `Failed to upload file to storage (${uploadResponse.status})`,
      );
    }

    return initiated.s3Key;
  };
};
